/**
 * Lazy client-side BCP-47 sanity check. Matches the backend regex in
 * `wanderfit-api/src/tos/dto/locale.dto.ts` (`^[a-z]{2,3}(-[A-Z]{2})?$`).
 * The UI uses this for instant feedback; backend is the source of truth
 * and rejects anything stricter with `400` (mapped to inline form errors).
 *
 * The supported subset is intentional and matches what backend accepts:
 *   - `xx`           two-letter ISO 639-1 language code (e.g. `pt`)
 *   - `xxx`          three-letter ISO 639-2/3 (e.g. `arz`)
 *   - `xx-YY`        language + ISO 3166-1 alpha-2 region (e.g. `es-AR`)
 *
 * Out of scope today (would 400 on backend): script subtags (`zh-Hant`),
 * UN M.49 region codes (`es-419`). If a market demands those, backend
 * extends the regex first, BO follows.
 */

const BCP47_PATTERN = /^[a-z]{2,3}(-[A-Z]{2})?$/;

export function isValidBcp47(code: string): boolean {
  return BCP47_PATTERN.test(code);
}

/**
 * Canonicalise raw user input toward the BCP-47 shape the backend
 * expects. Lowercases the language subtag and uppercases the region
 * subtag if present. Idempotent. Does NOT validate — feed the result
 * through `isValidBcp47` if you need a yes/no.
 */
export function canonicaliseBcp47(input: string): string {
  const trimmed = input.trim();
  if (trimmed.length === 0) return "";
  const [lang, region] = trimmed.split("-");
  if (!region) return lang.toLowerCase();
  return `${lang.toLowerCase()}-${region.toUpperCase()}`;
}
